/**
 * @file receipt-item-edit.ts
 * @description Правка строки черновика приёмки (phase19.31, Б16): начальные
 * значения формы из строки и тело `PATCH …/items/:itemId` — только
 * изменённые поля.
 *
 * Контракт сервера (`UpdateReceiptItemDto`, cabinet-api-contract §15):
 * `undefined` — поле не трогать, `null` — очистить. Отправляем только то, что
 * человек поменял: иначе сохранение «количества» заодно перезаписало бы срок,
 * который сервер досчитал сам.
 *
 * Цены (`unitCost`, `salePrice`) сравниваем как деньги: «450», «450,00» и
 * «450.00» — одно и то же, в запрос такое не уходит. Цену продажи кладём в
 * тело только когда она показана (`showSalePrice`): кладовщик её не видит
 * (§11), и пустое поле не должно читаться как «очистить».
 */

import type { ReceiptItemLotValue } from "./ReceiptItemLotFields";
import type { GoodsReceiptItemDto, receiptsApi } from "../../lib/api-wms";

/** Тело правки строки — ровно то, что принимает `receiptsApi.updateItem`. */
export type ReceiptItemPatch = Parameters<typeof receiptsApi.updateItem>[2];

/** Состояние формы правки: количество и поля партии (как у панели добавления). */
export interface ReceiptItemEditValue {
  quantity: string;
  lot: ReceiptItemLotValue;
}

/** Что показано в форме правки. */
export interface ReceiptItemEditOptions {
  /** Поле «Цена продажи» видно (не кладовщик, SC51). */
  showSalePrice: boolean;
}

/** Дата из DTO («2027-03-15T00:00:00.000Z») → значение поля «гггг-мм-дд». */
function dateValue(iso: string | null | undefined): string {
  return iso ? iso.slice(0, 10) : "";
}

/** Форма правки, заполненная текущими значениями строки. */
export function receiptItemEditInitial(item: GoodsReceiptItemDto): ReceiptItemEditValue {
  return {
    quantity: item.quantity,
    lot: {
      unitCost: item.unitCost ?? "",
      salePrice: item.salePrice ?? "",
      // Номер, введённый в строке; номер заведённой партии здесь не правится.
      batchNumber: item.batchNumber ?? "",
      binId: item.binId ?? "",
      manufacturedAt: dateValue(item.manufacturedAt),
      expiresAt: dateValue(item.expiresAt),
      // Срок уже записан — автосчёт от даты изготовления его не перетирает.
      expiresManual: Boolean(item.expiresAt),
    },
  };
}

/** Пустая строка поля → `null` (очистить), иначе значение без пробелов по краям. */
function orNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

/** Цена для сравнения: «450», «450,00» и «450.00» дают один ключ. */
function priceKey(value: string): string {
  const text = value.trim().replace(",", ".");
  if (!text) return "";
  const num = Number(text);
  return Number.isFinite(num) ? num.toFixed(2) : text;
}

/**
 * Проверка цены до отправки (то же правило, что у сервера): пусто — можно
 * (очистить), иначе неотрицательное число, запятая допустима.
 */
export function receiptItemPriceError(value: string): string | null {
  const text = value.trim().replace(",", ".");
  if (!text) return null;
  const num = Number(text);
  return Number.isFinite(num) && num >= 0 ? null : "Цена — неотрицательное число, например 450 или 450,50";
}

/**
 * Что изменилось относительно строки. Пустой объект — менять нечего
 * (кнопка «Сохранить» тогда неактивна).
 */
export function receiptItemEditPatch(
  item: GoodsReceiptItemDto,
  next: ReceiptItemEditValue,
  options: ReceiptItemEditOptions,
): ReceiptItemPatch {
  const before = receiptItemEditInitial(item);
  const patch: ReceiptItemPatch = {};
  if (next.quantity.trim() !== before.quantity.trim()) patch.quantity = next.quantity.trim();
  if (priceKey(next.lot.unitCost) !== priceKey(before.lot.unitCost)) patch.unitCost = orNull(next.lot.unitCost);
  if (options.showSalePrice && priceKey(next.lot.salePrice) !== priceKey(before.lot.salePrice)) {
    patch.salePrice = orNull(next.lot.salePrice);
  }
  if (next.lot.batchNumber.trim() !== before.lot.batchNumber.trim()) patch.batchNumber = orNull(next.lot.batchNumber);
  if (next.lot.binId !== before.lot.binId) patch.binId = orNull(next.lot.binId);
  if (next.lot.manufacturedAt !== before.lot.manufacturedAt) patch.manufacturedAt = orNull(next.lot.manufacturedAt);
  if (next.lot.expiresAt !== before.lot.expiresAt) patch.expiresAt = orNull(next.lot.expiresAt);
  return patch;
}
